"use client"

import React, {Suspense, useCallback, useEffect, useRef, useState} from "react";
import Image from 'next/image';

import accountSvg from "$/account.svg";
import {useAuthenticator} from "@aws-amplify/ui-react";
import {AuthenticatorProvider} from "@aws-amplify/ui-react-core";
import { Logo } from "$/logo";
import { fonts } from "@/stylingConstants";
import styles from './pageTitle.module.css'
import { usePathname,useRouter, useSearchParams } from "next/navigation";
import { useIsC1Integrated } from '@/components/C1Integration/useIsC1Integrated';


type Props = {
  title?: string;
  backUrl?: string | null;
  analyticsBackLabel?: string;
  titleBgColor?: string;
  paddingRight?:number;
  isLoggedIn?:boolean;
}

const Logout = React.forwardRef<HTMLButtonElement, { closeMenu: () => void }>(({ closeMenu }, ref) => {
  const { signOut, user } = useAuthenticator((context) => [context.user]);

  const logout = useCallback(() => {
    signOut();
    closeMenu();
  }, [signOut, closeMenu]);

  useEffect(() => {
    if (!user) {
      const urlStr = `/login?origin=${window.location.pathname}${window.location.search}`;
      window.location.replace(urlStr);
    }
  }, [user]);

  return (
    <button
      ref={ref}
      type="button"
      onClick={logout}
      className={`${styles.menuButton} ${styles.borderBottom}`}
      role="menuitem"
    >
      Log out
    </button>
  );
});

Logout.displayName = 'Logout';


const ReportLink = React.forwardRef<HTMLButtonElement, { closeMenu: () => void }>(({ closeMenu }, ref) => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  const fullPath = searchParams.toString()
    ? `${pathname}?${searchParams.toString()}`
    : pathname;

  const handleClick = () => {
    router.push(`/report?backUrl=${encodeURIComponent(fullPath)}`);
    closeMenu();
  };

  return (
    <button
      ref={ref}
      type="button"
      onClick={handleClick}
      className={`${styles.menuButton} ${styles.borderTop}`}
      role="menuitem"
    >
      Help us improve
    </button>
  );
});

ReportLink.displayName = 'ReportLink';

const PageTitleGigya = ({ titleBgColor = 'bg-white' }: Readonly<Props>) => {
  return <div className={`${styles.headerContainer} ${titleBgColor}`}>
    <div className={styles.headerInner}>
      <div style={{ marginRight: 'auto', display: 'flex', gap: 64 }}>
        <Logo/>
      </div>
    </div>
  </div>
}

const PageTitleCognito = ({ title, titleBgColor = 'bg-white', isLoggedIn }: Readonly<Props>) => {

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const accountButtonRef = useRef<HTMLButtonElement>(null);
  const firstMenuItemRef = useRef<HTMLButtonElement>(null);
  const lastMenuItemRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && ! menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      setTimeout(() => firstMenuItemRef.current?.focus(), 0);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  const handleAccountButtonClick = () => {
    setMenuOpen(!menuOpen);
  };

  const handleAccountButtonKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setMenuOpen(!menuOpen);
    }
  };

  const handleMenuKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setMenuOpen(false);
      accountButtonRef.current?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (document.activeElement === firstMenuItemRef.current) {
        lastMenuItemRef.current?.focus();
      } else {
        firstMenuItemRef.current?.focus();
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (document.activeElement === lastMenuItemRef.current) {
        firstMenuItemRef.current?.focus();
      } else {
        lastMenuItemRef.current?.focus();
      }
    } else if (e.key === 'Tab') {
      const isTabOutForward = !e.shiftKey && document.activeElement === lastMenuItemRef.current;
      const isTabOutBackward = e.shiftKey && document.activeElement === firstMenuItemRef.current;
      
      if (isTabOutForward || isTabOutBackward) {
        setMenuOpen(false);
      }
    }
  };

  const closeMenu = () => {
    setMenuOpen(false);
    accountButtonRef.current?.focus();
  };


  return (
      <AuthenticatorProvider>
        <div
            className={`${styles.headerContainer} ${titleBgColor}`}
        >
          <div className={styles.headerInner}>

            <div style={{ marginRight: 'auto', display: 'flex', gap: 64 }}>
              <Logo/>
              {title === 'admin' &&
                  <p style={fonts.bodyxl}>Speaking Companion authoring tool</p>
          }
          </div>
          {isLoggedIn && <div className='flex align-right relative'>
            <button
                ref={accountButtonRef}
                type="button"
                style={{cursor: "pointer", background: "none", border: "none", padding: 0, display: "flex", alignItems: "center"}}
                onClick={handleAccountButtonClick}
                onKeyDown={handleAccountButtonKeyDown}
                aria-label="Account menu"
                aria-expanded={menuOpen}
                aria-haspopup="true"
            >
              <Image
                  src={accountSvg}
                  alt=""
                  unoptimized
              />
            </button>
            {menuOpen && (
              <div
                ref={menuRef}
                className={styles.menuWrapper}
                role="menu"
                onKeyDown={handleMenuKeyDown}
                tabIndex={-1}
              >
                <Suspense fallback={null}>
                  <ReportLink ref={firstMenuItemRef} closeMenu={closeMenu} />
                </Suspense>
                <Logout ref={lastMenuItemRef} closeMenu={closeMenu} />
              </div>
            )}
          </div>}
        </div>
      </div>
    </AuthenticatorProvider>
    );
};

export default function PageTitle({title,titleBgColor = 'bg-white', isLoggedIn = true}: Readonly<Props>) {
  const isC1Integrated = useIsC1Integrated();
  if(isC1Integrated === null || isC1Integrated) {
    return <PageTitleGigya />; // Return the more basic header until isC1Integrated loads properly
  } else {
    return <PageTitleCognito title={title} titleBgColor={titleBgColor} isLoggedIn={isLoggedIn}/>;
  }
}